import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import apiClient, { getErrorMessage } from '../services/api-client';
import AuthService from '../services/auth';
import wsClient from '../services/ws';
import { isLocationUnset } from '../utils/location';

const ADMIN_TOUR_STAGE_KEY = 'mgo_admin_tour_stage_v1';

function sanitizeRoute(route) {
  if (!route || typeof route !== 'string') {
    return '/servicios';
  }
  if (!route.startsWith('/')) {
    return '/servicios';
  }
  if (route.startsWith('//')) {
    return '/servicios';
  }
  return route;
}

export default function AuthBootstrap() {
  const [searchParams] = useSearchParams();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const incomingToken = searchParams.get('token') || searchParams.get('jwtToken');
      const nextRoute = sanitizeRoute(searchParams.get('next'));
      const shouldStartAdminTour = searchParams.get('tour') === 'admin';

      if (shouldStartAdminTour) {
        window.sessionStorage.setItem(ADMIN_TOUR_STAGE_KEY, 'servicios');
      }

      if (incomingToken) {
        AuthService.setToken(incomingToken);
        // Schedule the resident onboarding carousel on first token-based entry
        // (deep link) for residents (habitante/receptor) when not yet completed.
        // Gated on the completion flag so we don't re-show it on every re-entry.
        // The role is derived directly from the incoming JWT (authoritative at
        // this moment) rather than re-reading from storage.
        try {
          const role = AuthService.getRoleFromToken(incomingToken);
          if (role === 'habitante' || role === 'receptor') {
            const alreadyDone = localStorage.getItem('mgo_onboarding_carousel_completed_v1') === '1';
            if (!alreadyDone) {
              sessionStorage.setItem('mgo_onboarding_carousel_pending_v1', '1');
              // Notify the OnboardingCarousel (mounted in the app shell) so it
              // re-reads the pending flag and auto-opens. Required for
              // returning users whose isTargetRole was already true on mount.
              window.dispatchEvent(new Event('mgo:onboarding-pending'));
            }
          }
        } catch (err) {
          console.warn('AuthBootstrap: failed to schedule onboarding carousel', err);
        }
      }

      if (!incomingToken && !AuthService.isLogged()) {
        if (!cancelled) {
          setRedirect({ trigger: true, route: '/login' });
        }
        return;
      }

      try {
        await apiClient.get('users/configs', {}, { action: 'bootstrap_user_configs' });
        wsClient.disconnect();
        wsClient.connect();

        // Org admins must configure the org location before continuing.
        // Gate only on a confirmed unset — if the dashboard config can't be
        // fetched, fall back to the normal redirect so the user isn't trapped.
        let targetRoute = nextRoute;
        const role = incomingToken
          ? AuthService.getRoleFromToken(incomingToken)
          : AuthService.getCurrentUserRole();
        if (role === 'admin') {
          const orgKey = AuthService.getCurrentUserOrgKey();
          if (orgKey) {
            try {
              const dashRes = await apiClient.get(`landing/dashboard/${orgKey}`, {}, { action: 'check_org_location' });
              const dashConfig = dashRes?.data?.data || {};
              if (isLocationUnset(dashConfig)) {
                targetRoute = `/setup-location?next=${encodeURIComponent(nextRoute)}`;
              }
            } catch (locationError) {
              console.warn('AuthBootstrap: location check failed, continuing to next route', locationError);
            }
          }
        }

        if (!cancelled) {
          setRedirect({ trigger: true, route: targetRoute });
        }
      } catch (bootstrapError) {
        AuthService.logout();
        if (!cancelled) {
          setError(getErrorMessage(bootstrapError, 'No se pudo preparar la sesión en la app.'));
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, paddingTop: 'env(safe-area-inset-top)', bgcolor: 'background.default' }}>
        <Card sx={{ width: '100%', borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, textAlign: 'center', '&:last-child': { pb: 2 } }}>
            {!error && <CircularProgress color="secondary" />}
            <Typography sx={{ fontSize: 24, fontWeight: 900, color: 'text.primary' }}>
              Preparando tu acceso
            </Typography>
            {!error && (
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
                Estamos iniciando tu sesión en MiGarita.Online.
              </Typography>
            )}
            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
